import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { t } from "../i18n";
import { listShopifyProductsPicker, listShopifyProductScores } from "../lib/api";
import {
  X,
  MagnifyingGlass,
  CircleNotch,
  CheckCircle,
  Package,
  CaretRight,
  Funnel,
} from "@phosphor-icons/react";

/**
 * Product picker modal. Lets the user paginate through the store's products
 * and tick which ones to include in the scan. Returns the selected product
 * IDs when the user clicks "Scan these N".
 *
 * Props:
 *   open: boolean
 *   connId: string
 *   lang: string
 *   onClose: () => void
 *   onConfirm: (ids: number[]) => void   // called with the selected IDs
 */
export default function ProductPickerModal({ open, connId, lang, onClose, onConfirm }) {
  const [pages, setPages] = useState([]); // list of pages: [{ products, next_page_info }]
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [query, setQuery] = useState("");
  const [scoresMap, setScoresMap] = useState({}); // { "<pid>": { score, scanned_at } }
  const [filterMode, setFilterMode] = useState("all"); // all | never | lt90 | lt70

  useEffect(() => {
    if (open && connId) {
      setPages([]);
      setCurrentPage(0);
      setPicked(new Set());
      setQuery("");
      setScoresMap({});
      setFilterMode("all");
      loadPage("");
      loadScores();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, connId]);

  const loadScores = async () => {
    try {
      const res = await listShopifyProductScores(connId);
      setScoresMap(res.scores || {});
    } catch {
      // Non-fatal — picker still works without scores
    }
  };

  const loadPage = async (pageInfo) => {
    setLoading(true);
    try {
      const res = await listShopifyProductsPicker(connId, { limit: 50, pageInfo });
      setPages((prev) => [...prev, { products: res.products || [], next_page_info: res.next_page_info }]);
      setCurrentPage((prev) => prev + (pageInfo ? 1 : 0));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  const currentPageData = pages[currentPage] || { products: [], next_page_info: null };
  const totalLoaded = pages.reduce((acc, p) => acc + p.products.length, 0);

  const filteredProducts = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return currentPageData.products.filter((p) => {
      if (needle && !(p.title || "").toLowerCase().includes(needle)) return false;
      const meta = scoresMap[String(p.id)];
      if (filterMode === "never") return !meta;
      if (filterMode === "lt90") return meta && typeof meta.score === "number" && meta.score < 90;
      if (filterMode === "lt70") return meta && typeof meta.score === "number" && meta.score < 70;
      return true;
    });
  }, [currentPageData, query, scoresMap, filterMode]);

  const togglePick = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectVisible = () => {
    const ids = filteredProducts.map((p) => p.id);
    setPicked((prev) => {
      const next = new Set(prev);
      ids.forEach((i) => next.add(i));
      return next;
    });
  };

  const unselectVisible = () => {
    const ids = new Set(filteredProducts.map((p) => p.id));
    setPicked((prev) => new Set([...prev].filter((i) => !ids.has(i))));
  };

  const clearAll = () => setPicked(new Set());

  const goNext = async () => {
    const next = pages[currentPage]?.next_page_info;
    if (next) {
      // Check if we already loaded the next page
      const alreadyLoaded = currentPage + 1 < pages.length;
      if (alreadyLoaded) {
        setCurrentPage(currentPage + 1);
      } else {
        await loadPage(next);
      }
    }
  };

  const goPrev = () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="product-picker-modal"
    >
      <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-neutral-900 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-900 bg-neutral-50">
          <div className="flex items-center gap-2">
            <Package size={18} weight="bold" />
            <h2 className="text-sm uppercase tracking-[0.18em] font-mono font-bold">
              {t(lang, "product_picker_title")}
            </h2>
            <span className="text-[10px] font-mono text-neutral-500">
              · {totalLoaded} {t(lang, "product_picker_loaded")} · {picked.size} {t(lang, "product_picker_selected")}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-neutral-200 ma-trans"
            data-testid="product-picker-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-neutral-200 bg-white flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px] flex items-center border border-neutral-900 px-2.5 h-9">
            <MagnifyingGlass size={14} className="text-neutral-500" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t(lang, "product_picker_filter_placeholder")}
              className="flex-1 px-2 h-full bg-transparent text-xs font-mono focus:outline-none"
              data-testid="product-picker-search"
            />
          </div>
          <div className="flex items-center border border-neutral-900 h-9">
            <span className="px-2 text-neutral-500"><Funnel size={13} weight="bold" /></span>
            <select
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
              className="h-full bg-transparent text-[11px] font-mono pr-3 focus:outline-none cursor-pointer"
              data-testid="product-picker-filter-mode"
              title={t(lang, "product_picker_filter_hint")}
            >
              <option value="all">{t(lang, "product_picker_filter_all")}</option>
              <option value="never">{t(lang, "product_picker_filter_never")}</option>
              <option value="lt90">{t(lang, "product_picker_filter_lt90")}</option>
              <option value="lt70">{t(lang, "product_picker_filter_lt70")}</option>
            </select>
          </div>
          <button
            type="button"
            onClick={selectVisible}
            className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-mono border border-neutral-900 hover:bg-neutral-100 ma-trans"
            data-testid="product-picker-select-visible"
          >
            {t(lang, "product_picker_select_visible")}
          </button>
          <button
            type="button"
            onClick={unselectVisible}
            className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-mono border border-neutral-900 hover:bg-neutral-100 ma-trans"
            data-testid="product-picker-unselect-visible"
          >
            {t(lang, "product_picker_unselect_visible")}
          </button>
          <button
            type="button"
            onClick={clearAll}
            disabled={picked.size === 0}
            className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-mono border border-neutral-900 hover:bg-neutral-100 disabled:opacity-40 ma-trans"
            data-testid="product-picker-clear"
          >
            {t(lang, "product_picker_clear")}
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 py-3" data-testid="product-picker-list">
          {loading && pages.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-xs font-mono text-neutral-500">
              <CircleNotch size={18} className="animate-spin mr-2" />
              {t(lang, "product_picker_loading")}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-xs font-mono text-neutral-500 italic py-8 text-center">
              {query ? t(lang, "product_picker_no_match") : t(lang, "product_picker_empty")}
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {filteredProducts.map((p) => {
                const isPicked = picked.has(p.id);
                const meta = scoresMap[String(p.id)];
                const sc = meta && typeof meta.score === "number" ? meta.score : null;
                const scoreClass =
                  sc === null
                    ? "border-neutral-300 bg-neutral-100 text-neutral-500"
                    : sc >= 90
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                    : sc >= 70
                    ? "border-amber-300 bg-amber-50 text-amber-800"
                    : "border-rose-300 bg-rose-50 text-rose-800";
                return (
                  <li
                    key={p.id}
                    onClick={() => togglePick(p.id)}
                    className={`flex items-center gap-3 py-2 px-2 cursor-pointer ma-trans ${
                      isPicked ? "bg-emerald-50" : "hover:bg-neutral-50"
                    }`}
                    data-testid={`product-picker-row-${p.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={isPicked}
                      onChange={() => togglePick(p.id)}
                      className="w-4 h-4 accent-emerald-700"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt=""
                        className="w-9 h-9 object-cover border border-neutral-200"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-9 h-9 bg-neutral-100 border border-neutral-200 flex items-center justify-center">
                        <Package size={14} className="text-neutral-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{p.title || "(untitled)"}</div>
                      <div className="text-[10px] font-mono text-neutral-500 truncate">
                        #{p.id} · {p.status || "active"}
                        {p.handle ? ` · ${p.handle}` : ""}
                      </div>
                    </div>
                    <span
                      className={`min-w-[42px] text-center text-[10px] font-mono font-bold border px-1.5 py-0.5 ${scoreClass}`}
                      title={meta ? `${t(lang, "product_picker_last_scanned")}: ${meta.scanned_at?.slice(0, 10) || "?"}` : t(lang, "product_picker_never_scanned")}
                      data-testid={`product-picker-score-${p.id}`}
                    >
                      {sc === null ? "—" : sc}
                    </span>
                    {isPicked && <CheckCircle size={16} weight="fill" className="text-emerald-700 flex-shrink-0" />}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Pagination */}
        <div className="px-5 py-2 border-t border-neutral-200 bg-neutral-50 flex items-center gap-2 text-[11px] font-mono">
          <button
            type="button"
            onClick={goPrev}
            disabled={currentPage === 0}
            className="px-2.5 py-1 border border-neutral-900 hover:bg-neutral-200 disabled:opacity-30 ma-trans"
            data-testid="product-picker-prev"
          >
            ← {t(lang, "product_picker_prev")}
          </button>
          <span className="text-neutral-500">
            {t(lang, "product_picker_page")} {currentPage + 1}
          </span>
          <button
            type="button"
            onClick={goNext}
            disabled={loading || !pages[currentPage]?.next_page_info}
            className="px-2.5 py-1 border border-neutral-900 hover:bg-neutral-200 disabled:opacity-30 ma-trans inline-flex items-center gap-1"
            data-testid="product-picker-next"
          >
            {loading ? <CircleNotch size={11} className="animate-spin" /> : t(lang, "product_picker_next")}
            <CaretRight size={11} />
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 border border-neutral-900 bg-white hover:bg-neutral-200 text-[10px] uppercase tracking-widest ma-trans"
            data-testid="product-picker-cancel"
          >
            {t(lang, "cancel")}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(Array.from(picked))}
            disabled={picked.size === 0}
            className="px-3 py-1.5 border border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-40 text-[10px] uppercase tracking-widest font-bold ma-trans"
            data-testid="product-picker-confirm"
          >
            {t(lang, "product_picker_confirm")} ({picked.size})
          </button>
        </div>
      </div>
    </div>
  );
}
